import Avatar from "@mui/material/Avatar";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { Typography, useTheme } from "@mui/material";
import { UserIcon } from "src/foundations/icons";
import {
    SenecFunctionalIconButton,
    SenecFunctionalIconButtonProps,
} from "src/components/button/senec-functional-button/SenecFunctionalIconButton";
import { styled } from "src/styles/fpp-theme/fppStyled";

export type SenecAvatarProps = {
    /**
     * Full name where the component can extract the initials from.
     * If not provided, a generic user icon will be displayed
     * */
    fullName?: string;
} & (
    | {
          asButton?: false;
      }
    | ({
          asButton: true;
      } & Omit<SenecFunctionalIconButtonProps, "level" | "colorTheme" | "size">)
);

/**
 * The `SenecAvatar` component offers two variants of that component.
 *
 * ##### Default variant
 * The default one is not interactive and a simple `Avatar` component by MUI.
 *
 * ##### Interactive variant
 * When providing the `asButton` prop this component acts like the `SenecFunctionalIconButton` component with the typical `onClick`, `disabled`, ... options.
 */
export const SenecAvatar = ({ fullName, asButton = false, ...buttonProps }: SenecAvatarProps) => {
    const theme: SenecFPPTheme = useTheme();

    if (asButton) {
        return (
            <Button size="medium" level="primary" {...buttonProps}>
                {getInitialsByFullName(fullName) ?? <UserIcon />}
            </Button>
        );
    }

    return (
        <Avatar
            variant="rounded"
            sx={{
                backgroundColor: theme.palette.azur50.main,
                borderRadius: "0.5rem",
            }}
            data-testid="user-avatar"
        >
            {getInitialsByFullName(fullName) ?? <UserIcon />}
        </Avatar>
    );
};

const Button = styled(SenecFunctionalIconButton)(({ theme }) => ({
    color: theme.palette.white.main,
    backgroundColor: theme.palette.azur50.main,
    "&:hover": {
        color: theme.palette.white.main,
        backgroundColor: theme.palette.azur60.main,
    },
    "&:active": {
        color: theme.palette.white.main,
        backgroundColor: theme.palette.azur90.main,
    },
    "&.Mui-disabled": {
        color: theme.palette.white.main,
        backgroundColor: theme.palette.primary.main,
        opacity: "0.7",
    },
}));

const getInitialsByFullName = (fullName?: string) => {
    if (fullName === undefined || fullName === "") {
        return;
    }

    const fullNameArr = fullName.split(" ");

    const lettersToDisplay: string[] = [];

    fullNameArr.forEach((letter) => {
        const firstLetter = letter.charAt(0);

        if (firstLetter) {
            lettersToDisplay.push(firstLetter);
        }
    });

    return <Typography variant="body1Medium">{lettersToDisplay.slice(0, 2).join("").toUpperCase()}</Typography>;
};
